// @ts-nocheck
import React from 'react';
import { AlertTriangle, RefreshCw, Home, Mail } from 'lucide-react';

class PaymentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and potentially to error reporting service
    console.error('Payment Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Enhanced error logging for usage-related errors
    if (this.props.context === 'usage') {
      console.error('Usage Error Details:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString()
      });
    }

    // In a real app, you would log this to an error reporting service
    // logErrorToService(error, errorInfo, this.props.context);
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback: CustomFallback, context = 'general' } = this.props;
      
      // If a custom fallback is provided, use it
      if (CustomFallback) {
        return (
          <CustomFallback 
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            retryCount={this.state.retryCount}
          />
        );
      }

      // Default error UI based on context
      return (
        <div className="rounded-xl bg-surface shadow-card border border-danger-border">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-danger-subtle rounded-lg">
                <AlertTriangle className="w-5 h-5 text-danger" />
              </div>
              <div>
                <h3 className="text-title-sm font-semibold text-content">
                  {context === 'payment' ? 'Payment Unavailable' :
                   context === 'usage' ? 'Usage Data Unavailable' : 'Something went wrong'}
                </h3>
                <p className="text-meta text-content-muted">
                  We're having trouble loading this section.
                </p>
              </div>
            </div>

            <div className="bg-danger-subtle border border-danger-border rounded-lg p-4 mb-4">
              <p className="text-ui text-danger-on-subtle">
                {context === 'payment'
                  ? 'There was an issue with the payment system. Please try again or contact support if the problem persists.'
                  : context === 'usage'
                  ? 'There was an issue loading your usage data. Please try again in a moment.'
                  : 'An unexpected error occurred. Please try again or contact support if the problem persists.'
                }
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-meta font-semibold text-danger-on-subtle hover:text-danger focus-ring rounded">
                    ▶ Debug Info (dev only)
                  </summary>
                  <div className="mt-2 p-3 bg-surface-sunken rounded-lg border border-line text-2xs font-mono text-content-secondary overflow-auto max-h-48">
                    <div className="mb-2">
                      <strong>Error:</strong> {this.state.error.toString()}
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre className="whitespace-pre-wrap mt-1">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-line-subtle">
              <button
                onClick={this.handleRetry}
                className="btn-primary text-ui px-4 py-2"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Try Again
              </button>
              
              <button
                onClick={this.handleReload}
                className="btn-secondary text-ui px-4 py-2"
              >
                <Home className="w-3.5 h-3.5" />
                Reload Page
              </button>
              
              <a
                href="/support"
                className="btn-secondary text-ui px-4 py-2"
              >
                <Mail className="w-3.5 h-3.5" />
                Contact Support
              </a>
            </div>

            {this.state.retryCount > 0 && (
              <div className="mt-3 p-3 bg-warning-subtle border border-warning-border rounded-lg" role="status">
                <p className="text-ui text-warning-on-subtle">
                  <strong>Retry attempt #{this.state.retryCount}</strong>
                  {this.state.retryCount >= 3 && (
                    <span className="block mt-1">
                      Multiple retry attempts detected. If the issue persists, please contact support.
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Specialized error boundaries for different payment contexts
export const PaymentMethodErrorBoundary = ({ children }) => (
  <PaymentErrorBoundary context="payment">
    {children}
  </PaymentErrorBoundary>
);

export const TransactionErrorBoundary = ({ children }) => (
  <PaymentErrorBoundary context="payment">
    {children}
  </PaymentErrorBoundary>
);

export const BillingErrorBoundary = ({ children }) => (
  <PaymentErrorBoundary context="payment">
    {children}
  </PaymentErrorBoundary>
);

export const UsageErrorBoundary = ({ children }) => (
  <PaymentErrorBoundary context="usage">
    {children}
  </PaymentErrorBoundary>
);

// Generic error display component for non-boundary errors
export const ErrorDisplay = ({ 
  error, 
  onRetry, 
  onDismiss,
  title = 'Error',
  showRetry = true,
  showDismiss = false,
  className = ''
}) => {
  const getErrorMessage = (error) => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.error) return error.error;
    return 'An unexpected error occurred';
  };

  const getActionableMessage = (error) => {
    const message = getErrorMessage(error).toLowerCase();
    
    if (message.includes('network') || message.includes('fetch')) {
      return 'Please check your internet connection and try again.';
    }
    if (message.includes('timeout')) {
      return 'The request timed out. Please try again.';
    }
    if (message.includes('payment') || message.includes('card')) {
      return 'There was an issue processing your payment. Please verify your payment information and try again.';
    }
    if (message.includes('validation') || message.includes('invalid')) {
      return 'Please check your input and try again.';
    }
    return 'Please try again or contact support if the problem persists.';
  };

  return (
    <div role="alert" className={`bg-danger-subtle border border-danger-border rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-danger mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <h4 className="text-ui-lg font-semibold text-danger-on-subtle mb-1">{title}</h4>
          <p className="text-ui text-danger-on-subtle mb-2">{getErrorMessage(error)}</p>
          <p className="text-meta text-danger-on-subtle/80">{getActionableMessage(error)}</p>
          
          {(showRetry || showDismiss) && (
            <div className="flex items-center gap-2 mt-3">
              {showRetry && onRetry && (
                <button
                  onClick={onRetry}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-danger text-white text-meta font-semibold hover:bg-danger-solid transition-colors focus-ring"
                >
                  <RefreshCw className="w-3 h-3" />
                  Try Again
                </button>
              )}
              {showDismiss && onDismiss && (
                <button
                  onClick={onDismiss}
                  className="px-3 py-1.5 rounded-lg text-danger text-meta font-semibold hover:bg-danger-subtle transition-colors focus-ring"
                >
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { PaymentErrorBoundary };
export default PaymentErrorBoundary;